import "./style.scss";

import CloudOffIcon from "@mui/icons-material/CloudOff";
import { Drawer, Tab, Tooltip } from "@mui/material";
import Plus from "assets/img/svg/plus";
import ExitModal from "components/NewForm/ExitModal";
import EmptyState from "components/shared/EmptyState";
import SettingsHeader from "components/shared/SettingsHeader";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getUserListByCompany } from "store/adminPanelSlice";
import { fetchIntegrations } from "store/integrationsStore";
import type { IStatementTemplate } from "store/partnerStatementsStore";
import { fetchPartnerStatements, resetGoogleSheetData } from "store/partnerStatementsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import GoogleTab from "./GoogleTab";
import StatementFormContainer from "./StatementFormContainer";

const PartnerStatementsSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const partnerStatements = useAppSelector((state) => state.partnerStatements.partnerStatements);
    const integrations = useAppSelector((state) => state.integrations.integrations);
    const users = useAppSelector((state) => state.adminPanel.users.company.data);
    const { googleSheetData } = useAppSelector((state) => state.partnerStatements);

    const [editStatementDrawer, setEditStatementDrawer] = useState<{
        open: boolean;
        statementData: IStatementTemplate | null;
    }>({ open: false, statementData: null });
    const [tabsValue, setTabsValue] = useState(0);
    const [exitModal, setExitModal] = useState({ open: false, isDirty: false });

    const onOpenNewStatement = () => {
        navigate("/settings/partner-statements/new");
    };

    useEffect(() => {
        if (!partnerStatements.length) dispatch(fetchPartnerStatements());
        if (!integrations.length) dispatch(fetchIntegrations());
        if (!users.length) dispatch(getUserListByCompany());
    }, [dispatch]);

    const onChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
        setTabsValue(newValue);
    };

    const onOpenStatementDrawer = (statementData: IStatementTemplate) => {
        setEditStatementDrawer({ open: true, statementData });
    };

    const onCloseStatementDrawer = () => {
        if (exitModal.isDirty) setExitModal((prev) => ({ ...prev, open: true }));
        else handleCloseEditMode();
    };

    const onExitModalConfirm = () => {
        setExitModal({ open: false, isDirty: false });
        handleCloseEditMode();
    };

    const handleCloseEditMode = () => {
        setEditStatementDrawer({ open: false, statementData: null });
        dispatch(resetGoogleSheetData());
    };

    return (
        <div className="partner-statements-settings">
            <SettingsHeader
                title={t("PartnerStatements.Title")}
                subtitle={t("PartnerStatements.Subtitle")}
                renderAction={() => (
                    <StyledButton buttonType="primary" onClick={onOpenNewStatement}>
                        <Plus /> {t("PartnerStatements.NewStatement")}
                    </StyledButton>
                )}
            />
            <div className="partner-statements-body">
                <StyledTabs value={tabsValue} onChange={onChangeTabs}>
                    <Tab label="Google" />
                    <TabCrealo />
                </StyledTabs>
                <div className="partner-statements-tab-content">
                    {tabsValue == 0 && (
                        <GoogleTab
                            partnerStatements={partnerStatements}
                            users={users}
                            integrations={integrations}
                            openStatementDrawer={onOpenStatementDrawer}
                        />
                    )}
                </div>
                {!partnerStatements.length && (
                    <div className="partner-statements-empty-state">
                        <EmptyState type="partnerStatement" />
                    </div>
                )}
            </div>
            <Drawer anchor="right" open={editStatementDrawer.open} onClose={onCloseStatementDrawer}>
                <ExitModal
                    open={exitModal.open}
                    onConfirm={onExitModalConfirm}
                    onCancel={() => setExitModal((prev) => ({ ...prev, open: false }))}
                />
                {editStatementDrawer.statementData && (
                    <StatementFormContainer
                        mode="edit"
                        type="statement"
                        onDirtyDrawer={(isDirty) => setExitModal((prev) => ({ ...prev, isDirty: isDirty }))}
                        statementData={editStatementDrawer.statementData}
                        onCloseEditMode={handleCloseEditMode}
                    />
                )}
            </Drawer>
        </div>
    );
};

export default PartnerStatementsSettings;

const TabCrealo = () => {
    const [t] = useTranslation();
    return (
        <Tooltip title={t("PartnerStatements.NotAvailableYet")} followCursor>
            <div>
                <Tab
                    label={
                        <div className="statement-tab-sage">
                            <CloudOffIcon />
                            Crealo
                        </div>
                    }
                    disabled
                />
            </div>
        </Tooltip>
    );
};
